import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Target, TrendingUp, Users, Shield } from 'lucide-react';
export function OrganizationalLevelPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-1429" data-mp="wjnmurzqjjz4">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1430" data-mp="wlgyklp7gudz">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1431" data-mp="10liyoqfalfr5">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1432" data-mp="1fi09ngjkksd6">
            <span className="w-8 h-px bg-teal" data-id="element-1433" data-mp="1jjlfihxos6dv"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1434" data-mp="1ji0miwr0svi4">
              Scale
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1435" data-mp="2c05yiey9a1tv"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1436" data-mp="2c2ip1gi2xkij">
            Organizational Level
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1437" data-mp="30p5swdcedh5g">
            At the organizational level, NCJI is designed to support readiness
            and capability-building inside institutions. It helps organizations
            understand the human factors that shape performance, stability,
            leadership development, and long-term resilience.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1438" data-mp="26gvfpgm7l0z">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1439" data-mp="24e9phmhwe22">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1440" data-mp="1z13l1telr1co">
            Rather than looking only at outputs or credentials, the framework
            helps reveal how reasoning, self-direction, emotional regulation,
            cooperation, and responsibility influence real institutional
            effectiveness.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1441" data-mp="gvnfnoxapy9a">
            Organizations depend on more than technical ability. They rely on
            people who can think clearly, handle pressure, communicate well,
            coordinate with others, and grow into broader responsibilities over
            time. NCJI helps make those capabilities more visible, more
            structured, and more actionable so institutions can better develop
            talent, strengthen teams, and build more reliable leadership
            pipelines.
          </p>
        </motion.div>
      </section>

      {/* Readiness Inside Institutions */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1442" data-mp="19kc8l0o76x9y">
        <div className="max-w-4xl mx-auto" data-id="element-1443" data-mp="1jpatng6bi2xt">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1444" data-mp="1ep69ng67aq2u">
            <div className="flex items-center gap-4 mb-6" data-id="element-1445" data-mp="22dza9pxrpm3s">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-1446" data-mp="1xew4u4dvldva">
                <Target size={24} data-id="element-1447" data-mp="1so626ywu3p4a" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1448" data-mp="1sml2kuvnxfzk">
                Readiness Inside Institutions
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1449" data-mp="2l60frb51sam6">
              NCJI helps organizations understand where readiness is strong and
              where it is still forming. This includes the ability of people
              within the institution to reason well, manage pressure, follow
              through, cooperate effectively, and act responsibly inside systems
              that require trust and coordination. That makes the framework
              relevant for role fit, advancement, succession, and long-range
              institutional strength.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Capability-Building and Development */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1450" data-mp="2om32zfdc0rcs">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1451" data-mp="2auoif43s0i3m">
          <div className="flex items-center gap-4 mb-6" data-id="element-1452" data-mp="37cuk533k9m1q">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-1453" data-mp="37efdwqi1x0yj">
              <TrendingUp size={24} data-id="element-1454" data-mp="vv9519qa1ldy" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1455" data-mp="qx7p2xo5e26p">
              Capability-Building and Development
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1456" data-mp="10o2bc21e91s5">
            A major value of NCJI at the organizational level is its ability to
            connect insight to practical development. It helps institutions
            identify the capabilities that need strengthening and connect them
            to more intentional pathways for coaching, support, training,
            mentorship, and structured responsibility. This makes the framework
            useful not only for assessment, but for building stronger people
            within stronger institutions.
          </p>
        </motion.div>
      </section>

      {/* Leadership Pipelines and Responsibility */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-1457" data-mp="1tcg6ma13lk00">
        <div className="max-w-4xl mx-auto" data-id="element-1458" data-mp="20fecx93ykyy8">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1459" data-mp="25er05uyqs81c">
            <div className="flex items-center gap-4 mb-6" data-id="element-1460" data-mp="1hqr9ev1fkhmr">
              <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-1461" data-mp="1mo9lguaqvcyz">
                <Users size={24} data-id="element-1462" data-mp="1rnchjp8qy8ut" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1463" data-mp="1rljaw9y4vomt">
                Leadership Pipelines and Responsibility
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1464" data-mp="yy5lcwwrlvcq">
              As people move into roles with greater consequence, organizations
              need more than confidence or visibility to judge readiness. NCJI
              supports a more rigorous understanding of leadership development
              by focusing on reasoning quality, consequence awareness, emotional
              regulation, communication, systems thinking, and responsibility at
              scale. This can help make succession and leadership cultivation
              more structured and more reliable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Institutional Resilience */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1465" data-mp="2thxf29ilpyw5">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1466" data-mp="2pgc4wvxgbypn">
          <div className="flex items-center gap-4 mb-6" data-id="element-1467" data-mp="2akw5ebe3ralv">
            <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-1468" data-mp="38o4qw58gcqc">
              <Shield size={24} data-id="element-1469" data-mp="79znchpovxp1" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-1470" data-mp="79pvspozxih8">
              Institutional Resilience
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1471" data-mp="3uep7mhf1o00w">
            Stronger institutions are built on a stronger human layer. NCJI
            helps organizations identify where capability gaps, weak support
            systems, or shallow leadership depth may be undermining stability
            and performance. Over time, this supports better continuity,
            stronger teams, more resilient leadership structures, and healthier
            institutional development.
          </p>
        </motion.div>
      </section>
    </div>;
}